import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { setFiltersApplied } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import ButtonLink from "components/common/Link";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Typography from "components/common/Typography";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getHoursAndMin, getListOfFilters } from "utils/CommonFunctions";
import MultiSelectFilter from "../home/multiSelectFilter";
import { fetchPlanStats, fetchPlans, setSMEFilters } from "../home/sme.slice";
import PlanStatsItem from "./planStatsItem";

const PlanStats = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const { isMobileScreen } = useContext(ThemeContext);
  const { planStats, plans, planStatsTotal, filters } = useAppSelector((state) => state.sme);
  const { list } = useAppSelector((state) => state.functionalAreas);
  const details: any = plans[0];
  // @ts-ignore
  const fnArea = list.filter((l: any) => details?.functionalAreaIds?.some((f: any) => l.id === f));
  const [hours, minutes] = getHoursAndMin(details?.timeEstimateMinutes);
  const { filtersApplied } = useAppSelector((state: any) => state.app);

  useEffect(() => {
    const props = {
      filter: {
        ids: [params?.id]
      },
      pagination: {
        page: 1,
        size: 1
      },
      isSortingDisabled: true
    };

    fetchPlans(props);
    dispatch(fetchPlanStats({ id: params?.listId, cmsId: params?.id }));

    return () => {
      dispatch(setSMEFilters({ ...filters, statsEngagement: [], planStatus: [] }));
      dispatch(setFiltersApplied(false));
    };
  }, []);

  const fetchMore = () => {
    setPage((pageNo) => pageNo + 1);
    dispatch(fetchPlanStats({ id: params?.listId, cmsId: params?.id, page: page + 1 }));
  };

  const handleFilterChange = (val: any, key: string) => {
    if (val.length > 0) {
      dispatch(setFiltersApplied(true));
    } else if (getListOfFilters().length === 1) dispatch(setFiltersApplied(false));

    const statuses = key === "planStatus" ? val.join(",") : filters?.planStatus.join(",");
    const engagements = key === "statsEngagement" ? val.join(",") : filters?.statsEngagement.join(",");

    dispatch(setSMEFilters({ ...filters, [key]: val.length ? val : [] }));
    dispatch(
      fetchPlanStats({
        id: params?.listId,
        cmsId: params?.id,
        status: statuses,
        engagement: engagements
      })
    );
    setPage(0);
  };

  return (
    <>
      <Stack
        spacing={2}
        direction={isMobileScreen ? "column" : "row"}
        sx={{
          paddingY: "32px"
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            showBold
            color={tokens.Text.General.PrimaryHeadline}
            sx={{
              flex: 1,
              marginBottom: "16px"
            }}
          >
            {details?.name}
          </Typography>
          <Stack direction="row" alignItems="center">
            <AccessTimeIcon sx={{ fontSize: "16px", color: tokens.Icon.General.Secondary }} />
            <Typography variant="body2" color={tokens.Text.General.Secondary} sx={{ marginX: "4px" }}>
              Effort:
            </Typography>
            <Typography showBold variant="body2" color={tokens.Text.General.Secondary}>
              {hours > 0 && `${hours} hours`}
              {hours > 0 && minutes > 0 && ` ${minutes} minutes`}
              {hours === 0 && `${minutes} minutes`}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ maxWidth: "360px" }}>
          {fnArea.map((fn: any) => (
            <Typography
              key={fn.id}
              variant="label"
              showBold
              color={tokens.Text.General.Secondary}
              sx={{
                textTransform: "capitalize",
                padding: "4px 16px",
                border: `1px solid ${tokens.Border.General.Secondary}`,
                borderRadius: "100px",
                marginRight: "8px",
                marginBottom: "8px",
                fontSize: "12px"
              }}
            >
              {fn.name}
            </Typography>
          ))}
        </Stack>
      </Stack>
      <Box sx={{ backgroundColor: tokens.Background.General.Primary, borderRadius: "12px", paddingBottom: "16px" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={2}
          sx={{
            padding: "24px",
            borderBottom: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`
          }}
        >
          {filtersApplied && (
            <Grid>
              {" "}
              <ButtonLink
                text="Clear all"
                sx={{ color: tokens.Text.General.Color }}
                textVariant="body2"
                underline="none"
                onClick={() => {
                  dispatch(
                    setSMEFilters({
                      innCode: [],
                      engagement: [],
                      statsEngagement: [],
                      planStatus: []
                    })
                  );
                  dispatch(
                    fetchPlanStats({
                      id: params?.listId,
                      cmsId: params?.id,
                      status: [],
                      engagement: []
                    })
                  );
                  dispatch(setFiltersApplied(false));
                }}
              />
            </Grid>
          )}
          <MultiSelectFilter
            label="Status"
            options={["New", "In Progress", "Completed"]}
            onSubmit={(v: any) => handleFilterChange(v, "planStatus")}
            selectedOptions={filters?.planStatus}
          />
          <MultiSelectFilter
            label="Engagement"
            options={["Low", "Medium", "High"]}
            onSubmit={(v: any) => handleFilterChange(v, "statsEngagement")}
            selectedOptions={filters?.statsEngagement}
          />
        </Stack>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            sx={{
              padding: "16px 24px",
              borderBottom: isMobileScreen ? "none" : `1px solid ${tokens.Border.General.Tertiary}`,
              marginBottom: "16px",
              "& .MuiTypography-lebel": {
                fontSize: "12px"
              }
            }}
          >
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Inncode ({planStats?.length})
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Status
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Progress
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Engagement
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Priority
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Due Date
            </Typography>
            <Typography variant="label" color={tokens.Text.General.Secondary} sx={{ flex: 1 }}>
              Completion Date
            </Typography>
          </Stack>
          <Box id="statsDiv" sx={{ height: "calc(100vh - 500px)", overflowY: "auto" }}>
            <Scroller
              dataLength={planStats?.length}
              loadMore={fetchMore}
              totalList={planStatsTotal}
              label=""
              scrollableTarget="statsDiv"
            >
              {planStats?.map((plan: any) => (
                <PlanStatsItem key={plan.id} data={plan} />
              ))}
            </Scroller>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PlanStats;
